import { Line2D, Point2D } from '@shared/models/geometry.model';
import { DraftShape, Shape, Storey } from '@shared/models/design/storey.model';
import { ArrayOrSingle, ItemOrItemId } from '@shared/models/type-helpers.model';
import {
  computePolygonArea,
  doPolygonsIntersect,
  doPolygonsOverlapInterior,
  computePolygonPerimeter,
  isPointInPolygon,
  isPolygon2D,
  isEqualPoints,
  isPolygonContainedInPolygon,
} from '../geometry/geometry.helpers';
import { designGuards } from './design-typeguards.helpers';
import {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import { getId } from '../object.helpers';
import { guards } from '../typeguards.helpers';
import { Coordinate } from '@shared/models/geography.model';
import { moveCoordinate } from '../geometry/coordinates.helpers';
import {
  getStoreyById,
  getStoreyIndex,
  getStoreys,
  getAllStoreys,
  doElevationRangesConflict,
  getStoreyElevationRange,
  getStoreysOverlappingElevationRange,
  type ElevationRange,
} from './storey.helpers';
import { sum } from '../math.helpers';
import {
  getCutoutShapes,
  getSolidShapes,
  getStoreyGfaExcludingCutouts,
} from './cutout.helpers';

type StoreyShapesById = Record<Storey['id'], Shape[]>;

/**
 * Solids of {@link buildings}, optionally only storeys that occupy {@link range}.
 */
const resolveBuildingSolids = (
  buildings: Building[] | undefined,
  range?: ElevationRange,
): Shape[] => {
  if (!buildings?.length) return [];
  if (range) {
    return getBuildingSolidShapesOverlappingElevation(buildings, range);
  }
  return buildings.flatMap(getBuildingSolidShapes);
};

/**
 * A type that we can use to get all points from (shapes, design, storey)
 */
type PointScope = ArrayOrSingle<Shape | DraftShape> | BuildingDesign | Storey;

/**
 * A flexible helper to get points from any kind of scope (shapes, design, storey)
 * @param shapeOrDesignOrStorey
 * @returns
 */
export const getAllPoints = (scope?: PointScope): Point2D[] => {
  // Nothing provided => return empty array
  if (guards.is.undefined(scope)) return [];

  // shape or draft shape => return points
  if ('points' in scope) {
    return scope.points;
  }

  // design or storey => return all shapes
  const shapes = guards.is.array(scope) ? scope : getAllShapes(scope);

  if (!shapes?.length) return [];

  // flatten points from all shapes
  return shapes.flatMap((shape) => shape.points);
};

export const isPointWithinShape = (
  point: Point2D,
  shape: Shape | DraftShape,
): boolean => {
  // Draft shapes are not considered finished yet
  if (designGuards.is.draftShape(shape)) {
    return false;
  }
  return isPointInPolygon(point, shape.points);
};

export const isPointInsideShapes = (
  point: Point2D,
  shapes: Shape[] | undefined,
): boolean => {
  if (!shapes?.length) return false;
  return shapes.some((shape) => isPointWithinShape(point, shape));
};

/**
 * Check if point is inside any of the shapes in the storey
 * @param point - The point to check
 * @param storey - The storey to check the point in
 * @returns True if the point is inside any of the shapes in the storey, false otherwise
 */
export const isPointInsideStoreyShapes = (
  point: Point2D,
  storey: Storey | undefined,
): boolean => isPointInsideShapes(point, storey?.shapes);

/**
 * True when {@link point} lies in a cutout hole on {@link storey}.
 * Used so courtyard interiors stay drawable for solids (DEV-3843).
 */
export const isPointInsideStoreyCutout = (
  point: Point2D,
  storey: Storey | undefined,
): boolean => {
  if (!storey) return false;
  return getCutoutShapes(storey).some(
    (cutout) =>
      isPolygon2D(cutout.points) &&
      isPointInPolygon(point, cutout.points, { inclusive: true }),
  );
};

/**
 * True when {@link polygon} lies fully inside any cutout on {@link storey}.
 */
export const isPolygonInsideAnyStoreyCutout = (
  polygon: Point2D[],
  storey: Storey,
): boolean =>
  getCutoutShapes(storey).some(
    (cutout) =>
      isPolygon2D(cutout.points) &&
      isPolygonContainedInPolygon(polygon, cutout.points),
  );

/**
 * True when {@link point} sits in occupied floor-plate material on {@link storey}.
 * Empty cutout interiors are free so courtyard houses can be drawn; a solid that
 * itself sits fully inside a cutout (courtyard house) still occupies its footprint
 * (DEV-3843).
 */
export const isPointInsideStoreySolidOccupancy = (
  point: Point2D,
  storey: Storey | undefined,
  options: { inclusive?: boolean } = {},
): boolean => {
  if (!storey) return false;
  const inclusive = options.inclusive ?? true;
  const containingSolids = getSolidShapes(storey).filter(
    (solid) =>
      isPolygon2D(solid.points) &&
      isPointInPolygon(point, solid.points, { inclusive }),
  );
  if (!containingSolids.length) return false;
  // Ring material is occupied; empty hole is free; hole-nested solids occupy.
  if (!isPointInsideStoreyCutout(point, storey)) return true;
  return containingSolids.some((solid) =>
    isPolygonInsideAnyStoreyCutout(solid.points, storey),
  );
};

/**
 * True when two non-cutout shapes may coexist despite intersecting: exactly
 * one lies fully inside a cutout hole (courtyard house vs outer ring).
 * Two solids both outside holes, or both inside the same hole, stay rejected.
 */
export const isSolidPairAllowedByCutoutHole = (
  first: Shape | DraftShape,
  second: Shape | DraftShape,
  storey: Storey,
): boolean => {
  if (first.cutout === true || second.cutout === true) return false;
  if (!isPolygon2D(first.points) || !isPolygon2D(second.points)) return false;
  const firstInHole = isPolygonInsideAnyStoreyCutout(first.points, storey);
  const secondInHole = isPolygonInsideAnyStoreyCutout(second.points, storey);
  return firstInHole !== secondInHole;
};

/**
 * Storeys of {@link buildings} that occupy {@link range}, or every storey when
 * no range is given.
 */
const resolveBuildingStoreys = (
  buildings: Building[] | undefined,
  range?: ElevationRange,
): Storey[] => {
  if (!buildings?.length) return [];
  if (!range) return buildings.flatMap((building) => building.storeys);
  return buildings.flatMap((building) =>
    getStoreysOverlappingElevationRange(building.storeys, range),
  );
};

/**
 * Get all shapes from the design or storey
 * @param designOrStorey - The design or storey to get the shapes from
 * @returns
 */
export const getAllShapes = (
  root: Parameters<typeof getStoreys>[0],
): Shape[] => {
  const storeys = designGuards.is.buildingDesign(root)
    ? getAllStoreys(root)
    : getStoreys(root);
  return storeys.flatMap((s) => s.shapes);
};

/**
 * Determine if two shapes intersect or share a boundary.
 * @param first First {@link Shape} or {@link DraftShape}
 * @param second Second {@link Shape} or {@link DraftShape}
 * @returns true when polygons overlap
 */
export const doShapesIntersect = (
  first: Shape | DraftShape,
  second: Shape | DraftShape,
): boolean => {
  if (!isPolygon2D(first.points) || !isPolygon2D(second.points)) {
    return false;
  }
  return doPolygonsIntersect(first.points, second.points);
};

/**
 * Solid (non-cutout) shapes from every storey of {@link building}.
 * Empty buildings contribute nothing — hit-target squares are not geometry.
 */
export const getBuildingSolidShapes = (
  building: Building | undefined,
): Shape[] => {
  if (!building) return [];
  return building.storeys.flatMap((storey) => getSolidShapes(storey));
};

/**
 * True when two storeys share interior solid area in plan (XY).
 * Shared edges are allowed; see {@link doPolygonsOverlapInterior}.
 * Outer ring ↔ courtyard-house pairs are exempt via
 * {@link isSolidPairAllowedByCutoutHole}; two solids both inside the same
 * hole still count as overlap (DEV-3843).
 */
const doStoreySolidsOverlapInterior = (
  first: Storey,
  second: Storey,
): boolean => {
  const firstSolids = getSolidShapes(first);
  const secondSolids = getSolidShapes(second);
  return firstSolids.some(
    (a) =>
      isPolygon2D(a.points) &&
      secondSolids.some((b) => {
        if (!isPolygon2D(b.points)) return false;
        if (!doPolygonsOverlapInterior(a.points, b.points)) return false;
        // Only outer ↔ hole-nested is free; house ↔ house in the hole is not.
        if (isSolidPairAllowedByCutoutHole(a, b, first)) return false;
        if (isSolidPairAllowedByCutoutHole(a, b, second)) return false;
        return true;
      }),
  );
};

/**
 * Vertical span covering every storey in {@link storeys} (min bottom → max top).
 */
const getStoreysElevationEnvelope = (
  root: Storey[],
  storeys: Storey[],
): ElevationRange | undefined => {
  if (!storeys.length) return undefined;
  let bottom = Infinity;
  let top = -Infinity;
  for (const storey of storeys) {
    const range = getStoreyElevationRange(root, storey);
    bottom = Math.min(bottom, range.bottom);
    top = Math.max(top, range.top);
  }
  return { bottom, top };
};

/**
 * True when two buildings occupy the same volume: overlapping plan and either
 * overlapping height or a vertical gap between their plan-overlapping masses.
 * Shared party walls and slab-to-slab stacking are allowed. A hovering stack
 * with a gap in Z is not. Two covering buildings that overlap each other on a
 * shared roof are not — see {@link doElevationRangesConflict}.
 *
 * Elevation is compared on the envelope of storeys that share plan interior —
 * not pairwise — so an upper floor over a basement is not treated as hovering
 * when intermediate storeys of the same building fill the vertical span.
 */
export const doBuildingSolidsIntersect = (
  first: Building,
  second: Building,
): boolean => {
  if (!first.storeys.length || !second.storeys.length) return false;

  const firstOverlapping: Storey[] = [];
  const secondOverlapping: Storey[] = [];
  for (const storey of first.storeys) {
    let overlapsSecond = false;
    for (const other of second.storeys) {
      if (!doStoreySolidsOverlapInterior(storey, other)) continue;
      overlapsSecond = true;
      if (!secondOverlapping.includes(other)) secondOverlapping.push(other);
    }
    if (overlapsSecond) firstOverlapping.push(storey);
  }
  if (!firstOverlapping.length) return false;

  const firstEnvelope = getStoreysElevationEnvelope(
    first.storeys,
    firstOverlapping,
  );
  const secondEnvelope = getStoreysElevationEnvelope(
    second.storeys,
    secondOverlapping,
  );
  if (!firstEnvelope || !secondEnvelope) return false;

  return doElevationRangesConflict(firstEnvelope, secondEnvelope);
};

/**
 * True when {@link point} sits strictly inside solid floor-plate material of
 * {@link buildings} (outer solids minus cutout holes). Boundary points are
 * allowed so a draft can snap to a sibling wall. Pass {@link range} to ignore
 * storeys that do not occupy that height.
 */
export const isPointInsideBuildingSolids = (
  point: Point2D,
  buildings: Building[] | undefined,
  range?: ElevationRange,
): boolean =>
  resolveBuildingStoreys(buildings, range).some((storey) =>
    isPointInsideStoreySolidOccupancy(point, storey, { inclusive: false }),
  );

/**
 * True when {@link shape} shares interior area with solid floor-plate material
 * of {@link buildings}. Overlap with an outer ring is ignored when the shape
 * sits fully in a cutout hole, but overlap with another solid already in that
 * hole still counts (DEV-3843). Shared edges are not interior overlap — see
 * {@link doPolygonsOverlapInterior}. Pass {@link range} to ignore storeys that
 * do not occupy that height.
 */
export const doesShapeIntersectBuildingSolids = (
  shape: Shape | DraftShape,
  buildings: Building[] | undefined,
  range?: ElevationRange,
): boolean => {
  if (!isPolygon2D(shape.points)) return false;
  return resolveBuildingStoreys(buildings, range).some((storey) =>
    getSolidShapes(storey).some((solid) => {
      if (!isPolygon2D(solid.points)) return false;
      if (!doPolygonsOverlapInterior(shape.points, solid.points)) return false;
      // Outer ↔ courtyard-house only; still collide with solids in the hole.
      if (isSolidPairAllowedByCutoutHole(shape, solid, storey)) return false;
      return true;
    }),
  );
};

/**
 * Edges of sibling building solids, for the same snap/boundary flow as
 * other shapes on the selected storey.
 * Pass {@link range} to ignore storeys that do not occupy that height.
 */
export const getBuildingSolidEdges = (
  buildings: Building[] | undefined,
  range?: ElevationRange,
): { edge: Line2D; shapeId: string }[] => {
  const edges: { edge: Line2D; shapeId: string }[] = [];
  for (const shape of resolveBuildingSolids(buildings, range)) {
    const points = shape.points;
    if (points.length < 2) continue;
    for (let i = 0; i < points.length; i++) {
      const start = points[i];
      const end = points[(i + 1) % points.length];
      if (!start || !end) continue;
      edges.push({ edge: [start, end], shapeId: shape.id });
    }
  }
  return edges;
};

/**
 * Solid shapes on sibling buildings whose storeys occupy {@link range}.
 * Used so snap can follow neighbour walls at the drawing height only.
 */
export const getBuildingSolidShapesOverlappingElevation = (
  buildings: Building[] | undefined,
  range: ElevationRange,
): Shape[] => {
  if (!buildings?.length) return [];
  return buildings.flatMap((building) =>
    getStoreysOverlappingElevationRange(building.storeys, range).flatMap(
      (storey) => getSolidShapes(storey),
    ),
  );
};

/**
 * True when a proposed cutout polygon intersects any cutout already on the storey.
 * Mirrors the cutout–cutout branch of storey overlap validation (SHAPES_OVERLAP).
 * @param storey Storey before the candidate is appended
 * @param candidate Proposed shape (typically not yet appended to the storey)
 */
export const cutoutIntersectsExistingCutoutOnStorey = (
  storey: Storey,
  candidate: Shape | DraftShape,
): boolean => {
  if (candidate.cutout !== true) return false;
  return getCutoutShapes(storey).some((existing) =>
    doShapesIntersect(candidate, existing),
  );
};

/**
 * Gross floor area of the given storeys (cutouts excluded).
 * Pass a building's storeys, a single storey, or {@link getAllStoreys} — not a
 * {@link BuildingDesign}. Use {@link getDesignGfa} for the whole design.
 */
export const getGfaFromStoreys = (
  root: ArrayOrSingle<Storey> | undefined,
): number => {
  return sum(
    getStoreys(root).map((storey) => getStoreyGfaExcludingCutouts(storey)),
  );
};

/**
 * Gross floor area of every {@link Building} in the design (DEV-2576).
 * Use this for project-level GFA (lists, metadata, design expression summary).
 * @param design - Building design
 */
export const getDesignGfa = (design: BuildingDesign | undefined): number =>
  getGfaFromStoreys(getAllStoreys(design));

export const getPerimeterFromStoreys = (
  root: ArrayOrSingle<Storey> | undefined,
): number => {
  return sum(
    getStoreys(root).map((storey) =>
      getPerimeterFromShapes(
        ...storey.shapes.filter((shape) => shape.cutout !== true),
      ),
    ),
  );
};

export const getPerimeterFromShapes = (...shapes: Shape[]): number =>
  sum(shapes.map((shape) => computePolygonPerimeter(shape.points)));

export const getGfaFromShapes = (...shapes: Shape[]): number =>
  sum(shapes.map((shape) => computePolygonArea(shape.points)));

/**
 * Get a shape by id from all shapes in the design
 * @param design - The design to get the shape from
 * @param shapeOrId - The shape or id of the shape to get
 * @returns The shape or undefined if not found
 */
export const getShapeById = (
  designOrStorey: BuildingDesign | Storey | undefined,
  shapeOrId: ItemOrItemId<Shape>,
): Shape | undefined => {
  const id = getId(shapeOrId);
  return getAllShapes(designOrStorey).find((s) => s.id === id);
};

/**
 * Get a shape in storey with a point (in a certain position)
 * @param param0
 * @returns
 */
export const getShapeByPoint = (
  storey: Storey,
  point: Point2D,
): Shape | undefined =>
  storey.shapes.find((shape) =>
    shape.points.some((p) => isEqualPoints(p, point)),
  );

export const getIndexOfPoint = (shape: Shape, point: Point2D): number =>
  shape.points.findIndex((p) => isEqualPoints(p, point));

export const point2dsToCoordinates = (
  origin: Coordinate,
  scope: PointScope,
): Coordinate[] => {
  return getAllPoints(scope).map(([x, y]) => {
    // x: meters east(+)/west(-)
    const afterX = moveCoordinate(
      origin,
      [Math.abs(x), 'meters'],
      [x >= 0 ? 90 : 270, 'degrees'],
    );
    // y: canvas meters down(+)/up(-) -> south(+)/north(-)
    const afterY = moveCoordinate(
      afterX,
      [Math.abs(y), 'meters'],
      [y >= 0 ? 180 : 0, 'degrees'],
    );
    return afterY;
  });
};

/** Closed geographic ring used by both Design's volume silhouette and map previews. */
export const getShapeRingCoordinates = (
  shape: Shape,
  origin: Coordinate,
): [number, number][] => {
  const coordinates = point2dsToCoordinates(origin, shape).map(
    ({ lng, lat }): [number, number] => [lng, lat],
  );
  const first = coordinates[0];
  if (!first) return [];
  const last = coordinates[coordinates.length - 1];
  if (!last || first[0] !== last[0] || first[1] !== last[1]) {
    coordinates.push([first[0], first[1]]);
  }
  return coordinates;
};

/**
 * Collect shapes intersecting a removed shape for each higher storey.
 * Courtyard-house solids (fully inside a cutout on the base storey) only
 * cascade to other hole-nested solids above — not outer plates or host cutouts
 * (DEV-3843).
 * @param removedShape The base {@link Shape} being removed
 * @param storeys Ordered list of {@link Storey} to scan
 * @param baseIndex Index of the base storey in {@link storeys}
 * @returns Record keyed by storey id with intersecting shapes to remove
 */
export const getIntersectingShapesByStorey = (
  removedShape: Shape,
  storeys: Storey[],
  baseIndex: number,
): StoreyShapesById => {
  if (!removedShape.points?.length) return {};
  const baseStorey = storeys[baseIndex];
  // Hole-nested solid must not punch out the building stack above.
  const removedIsCourtyardHouse =
    !!baseStorey &&
    removedShape.cutout !== true &&
    isPolygon2D(removedShape.points) &&
    isPolygonInsideAnyStoreyCutout(removedShape.points, baseStorey);

  return storeys.reduce<StoreyShapesById>((acc, candidateStorey, index) => {
    if (index <= baseIndex) return acc;
    if (!candidateStorey?.shapes?.length) return acc;

    const intersectingShapes = candidateStorey.shapes.filter((candidate) => {
      if (!doShapesIntersect(removedShape, candidate)) return false;
      if (!removedIsCourtyardHouse) return true;
      if (candidate.cutout === true) return false;
      if (!isPolygon2D(candidate.points)) return false;
      return isPolygonInsideAnyStoreyCutout(candidate.points, candidateStorey);
    });
    if (!intersectingShapes.length) return acc;

    acc[candidateStorey.id] = intersectingShapes;
    return acc;
  }, {});
};

/**
 * Collect cutouts on higher storeys that intersect the seed cutouts transitively.
 * Mezzanine storeys are skipped (same rule as cutout drag propagation).
 * @param storeys Bottom-to-top order
 * @param seeds Cutouts to start the chain from (same storey or already queued)
 */
export const collectTransitiveLinkedCutoutsAbove = (
  storeys: Storey[],
  seeds: readonly { storeyIndex: number; cutout: Shape }[],
): StoreyShapesById => {
  const result: StoreyShapesById = {};
  const queue = [...seeds];
  // Track cutouts we already expanded upward from (not merely queued).
  const expanded = new Set<string>();

  const key = (storeyId: Storey['id'], shapeId: Shape['id']) =>
    `${storeyId}:${shapeId}`;

  while (queue.length) {
    const item = queue.shift();
    if (!item) break;
    const { storeyIndex, cutout } = item;
    const baseStorey = storeys[storeyIndex];
    if (!baseStorey) continue;
    const itemKey = key(baseStorey.id, cutout.id);
    if (expanded.has(itemKey)) continue;
    expanded.add(itemKey);

    for (let i = storeyIndex + 1; i < storeys.length; i += 1) {
      const above = storeys[i];
      if (!above?.shapes?.length) continue;
      if (designGuards.is.mezzanineStorey(above)) continue;

      for (const candidate of above.shapes) {
        if (candidate.cutout !== true) continue;
        if (!doShapesIntersect(cutout, candidate)) continue;
        const existing = result[above.id] ?? [];
        if (existing.some((s) => s.id === candidate.id)) continue;
        result[above.id] = [...existing, candidate];
        queue.push({ storeyIndex: i, cutout: candidate });
      }
    }
  }

  return result;
};

type LinkedPoints = {
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  pointIndex: number;
};

type LinkedPointsParams = {
  storeys: Storey[];
  storeyId: Storey['id'];
  shapeId: Shape['id'];
  pointIndex: number;
};

/**
 * Get all points in storey above on the same position
 * @param param0
 * @returns
 */
export const getLinkedPoints = ({
  storeys,
  storeyId,
  shapeId,
  pointIndex,
}: LinkedPointsParams): LinkedPoints[] => {
  const baseStorey = getStoreyById(storeys, storeyId);

  const baseShape = getShapeById(baseStorey, shapeId);
  const basePoint = baseShape?.points?.[pointIndex];
  if (!baseStorey || !baseShape || !basePoint) return [];
  if (baseStorey.mezzanine)
    return [{ storeyId: baseStorey.id, shapeId, pointIndex }];

  const baseStoreyIndex = getStoreyIndex(storeys, baseStorey);

  return [
    // ...baseStoreyPoints,
    ...storeys
      .map((storey, index) => {
        // Skip storeys below the base storey
        if (index < baseStoreyIndex) return undefined;
        const shape = getShapeByPoint(storey, basePoint);
        if (!shape) return undefined;
        return {
          storeyId: storey.id,
          shapeId: shape.id,
          pointIndex: getIndexOfPoint(shape, basePoint),
        };
      })
      .filter(guards.is.defined),
  ];
};
